import { sortBy } from "lodash"

import {
  PORTFOLIO_AUTHOR_KIND,
  type PortfolioProfileStored,
  type PortfolioProjectStored,
  type PortfolioReferenceStored,
  type PortfolioStageStored,
  type PortfolioStatementStored,
  type PortfolioTimelineEntryStored,
  type StudentPortfolioConstructor,
} from "#customTypes/composedTypes/portfolioTypes"
import { type Uuid } from "#customTypes/globalTypes"

import { sortProjectsByArrangement } from "./portfolioArrangement"

import type {
  PortfolioMockProfile,
  PortfolioMockProject,
  PortfolioMockReference,
  PortfolioMockStage,
  PortfolioMockStatement,
  PortfolioTimelineItem,
} from "./portfolioUiTypes"

const emptyStatementUi = (): PortfolioMockStatement => ({
  value: "",
  status: "draft",
  teacherFeedback: "",
})

export const createDefaultPortfolioProfile = (): PortfolioMockProfile => ({
  canvaLink: "",
  statements: {
    sustainedInquiryQuestion: emptyStatementUi(),
    sustainedPracticeEvidence: emptyStatementUi(),
  },
})

const statementStoredToUi = (
  s?: PortfolioStatementStored
): PortfolioMockStatement => ({
  value: s?.value ?? "",
  status: (s?.status as PortfolioMockStatement["status"]) ?? "draft",
  teacherFeedback: "",
  submittedAt: s?.submittedAt,
  submittedBy: s?.submittedBy,
  reviewedAt: s?.reviewedAt,
  reviewedBy: s?.reviewedBy,
})

const statementUiToStored = (
  s: PortfolioMockStatement
): PortfolioStatementStored => ({
  value: s.value,
  status: s.status as PortfolioStatementStored["status"],
  submittedAt: s.submittedAt,
  submittedBy: s.submittedBy as Uuid | undefined,
  reviewedAt: s.reviewedAt,
  reviewedBy: s.reviewedBy as Uuid | undefined,
})

const profileStoredToUi = (p: PortfolioProfileStored): PortfolioMockProfile => {
  const defaults = createDefaultPortfolioProfile()
  return {
    canvaLink: p.canvaLink ?? defaults.canvaLink,
    statements: {
      sustainedInquiryQuestion: {
        ...defaults.statements.sustainedInquiryQuestion,
        ...statementStoredToUi(p.sustainedInquiryQuestion),
      },
      sustainedPracticeEvidence: {
        ...defaults.statements.sustainedPracticeEvidence,
        ...statementStoredToUi(p.sustainedPracticeEvidence),
      },
    },
  }
}

const profileUiToStored = (
  p: PortfolioMockProfile
): PortfolioProfileStored => ({
  canvaLink: p.canvaLink,
  sustainedInquiryQuestion: statementUiToStored(
    p.statements.sustainedInquiryQuestion
  ),
  sustainedPracticeEvidence: statementUiToStored(
    p.statements.sustainedPracticeEvidence
  ),
})

const stageStoredToUi = (stage: PortfolioStageStored): PortfolioMockStage => ({
  id: stage.id,
  label: stage.label,
  status: stage.status,
  dueDate: stage.dueDate,
  artworkId: stage.artworkId,
  medium: stage.medium ?? "",
  notes: stage.notes,
  teacherComment: "",
  feedback: stage.feedback ?? "",
  rejectionReason: undefined,
  lastActivityAt: stage.lastActivityAt,
  planningGroup: stage.planningGroup,
  sortIndex: stage.sortIndex,
  imageUrl: stage.imageUrl,
  createdAt: stage.createdAt,
  createdBy: stage.createdBy,
  customFields: stage.customFields ?? {},
})

const stageUiToStored = (stage: PortfolioMockStage): PortfolioStageStored => ({
  id: stage.id as Uuid,
  label: stage.label,
  status: stage.status,
  dueDate: stage.dueDate,
  artworkId: stage.artworkId as Uuid | undefined,
  imageUrl: stage.imageUrl,
  medium: stage.medium,
  notes: stage.notes,
  feedback: stage.feedback,
  sortIndex: stage.sortIndex,
  planningGroup: stage.planningGroup,
  lastActivityAt: stage.lastActivityAt,
  createdAt: stage.createdAt,
  createdBy: stage.createdBy as Uuid | undefined,
  customFields: stage.customFields,
})

const refStoredToUi = (
  r: PortfolioReferenceStored
): PortfolioMockReference => ({
  id: r.id,
  imageUrl: r.imageUrl,
  title: r.title,
  notes: r.notes,
  teacherComment: "",
})

const refUiToStored = (
  r: PortfolioMockReference
): PortfolioReferenceStored => ({
  id: r.id as Uuid,
  imageUrl: r.imageUrl,
  title: r.title,
  notes: r.notes,
})

const projectStoredToUi = (
  project: PortfolioProjectStored
): PortfolioMockProject => {
  const stages = sortBy(
    Object.values(project.stages ?? {}),
    (s) => s.sortIndex ?? 0
  )
  const references = project.references
    ? Object.values(project.references).map(refStoredToUi)
    : undefined
  return {
    id: project.id,
    title: project.title,
    category: project.category,
    coverImageUrl: project.coverImageUrl,
    notes: project.notes,
    planningGroup: project.planningGroup,
    categorySortIndex: project.categorySortIndex,
    // `cleanFormData` stores an empty array as `null`; normalise both to [].
    tagIds: Array.isArray(project.tagIds) ? project.tagIds : [],
    lastActivityAt: project.lastActivityAt,
    dimensions: project.dimensions,
    materials: project.materials,
    processes: project.processes,
    ideas: project.ideas,
    customFields: {},
    references,
    createdAt: project.createdAt,
    createdBy: project.createdBy,
    stages: stages.map(stageStoredToUi),
  }
}

const projectUiToStored = (
  project: PortfolioMockProject
): PortfolioProjectStored => {
  const stagesRecord: Record<Uuid, PortfolioStageStored> = {}
  project.stages.forEach((stage, index) => {
    const sid = stage.id as Uuid
    stagesRecord[sid] = {
      ...stageUiToStored(stage),
      sortIndex: stage.sortIndex ?? index,
    }
  })
  const referencesRecord: Record<Uuid, PortfolioReferenceStored> | undefined =
    project.references?.length
      ? Object.fromEntries(
          project.references.map((r) => [r.id as Uuid, refUiToStored(r)])
        )
      : undefined
  return {
    id: project.id as Uuid,
    classId: undefined,
    title: project.title,
    category: project.category,
    coverImageUrl: project.coverImageUrl,
    notes: project.notes,
    planningGroup: project.planningGroup,
    categorySortIndex: project.categorySortIndex,
    tagIds: project.tagIds.length ? project.tagIds : undefined,
    lastActivityAt: project.lastActivityAt,
    createdAt: project.createdAt,
    createdBy: project.createdBy as Uuid | undefined,
    dimensions: project.dimensions,
    materials: project.materials,
    processes: project.processes,
    ideas: project.ideas,
    stages: stagesRecord,
    references: referencesRecord,
  }
}

const timelineStoredToUi = (
  entries: PortfolioTimelineEntryStored[]
): PortfolioTimelineItem[] =>
  entries.map((e) => ({
    id: e.id,
    type: e.type,
    projectId: e.projectId,
    message: e.message,
    createdAt: e.createdAt,
    createdBy: e.createdBy,
    authorKind: e.authorKind ?? PORTFOLIO_AUTHOR_KIND.TEACHER,
    eventScope: e.eventScope,
    eventEntityId: e.eventEntityId,
    eventFieldKey: e.eventFieldKey,
    eventNextValue: e.eventNextValue,
  }))

const timelineUiToStored = (
  entries: PortfolioTimelineItem[]
): PortfolioTimelineEntryStored[] =>
  entries.map((e) => ({
    id: e.id as Uuid,
    type: e.type,
    createdAt: e.createdAt,
    createdBy: e.createdBy as Uuid,
    authorKind: e.authorKind ?? PORTFOLIO_AUTHOR_KIND.TEACHER,
    message: e.message,
    projectId: e.projectId as Uuid | undefined,
    eventScope: e.eventScope,
    eventEntityId: e.eventEntityId as Uuid | undefined,
    eventFieldKey: e.eventFieldKey,
    eventNextValue: e.eventNextValue,
  }))

export const studentPortfolioDocToViewModel = (
  doc: StudentPortfolioConstructor | null
): {
  projects: PortfolioMockProject[]
  portfolioProfile: PortfolioMockProfile
  timelineEntries: PortfolioTimelineItem[]
  priorityLaneProjectIds: string[]
} => {
  if (!doc) {
    return {
      projects: [],
      portfolioProfile: createDefaultPortfolioProfile(),
      timelineEntries: [],
      priorityLaneProjectIds: [],
    }
  }
  const projectsMap = doc.projects ?? {}
  const orderList = doc.projectOrder ?? []
  const order = orderList.length ? orderList : Object.keys(projectsMap)
  // `projectOrder` is the incoming order; any project it forgot (a scanner
  // write that only touched `projects`) is appended so it is never hidden.
  const orderedIds = [
    ...order,
    ...Object.keys(projectsMap).filter((pid) => !order.includes(pid as Uuid)),
  ]
  const projects = orderedIds
    .map((pid) => projectsMap[pid as Uuid])
    .filter(Boolean)
    .map((p) => projectStoredToUi(p))
  // Category, then per-category index; ties keep `projectOrder` position.
  const normalized = normalizeProjectsForUi(sortProjectsByArrangement(projects))
  return {
    projects: normalized,
    portfolioProfile: profileStoredToUi(doc.profile ?? {}),
    timelineEntries: timelineStoredToUi(doc.timelineEntries ?? []),
    priorityLaneProjectIds: doc.priorityLaneProjectIds ?? [],
  }
}

export const viewModelToStudentPortfolioDoc = (
  studentId: Uuid,
  projects: PortfolioMockProject[],
  portfolioProfile: PortfolioMockProfile,
  timelineEntries: PortfolioTimelineItem[],
  priorityLaneProjectIds: string[],
  existing: StudentPortfolioConstructor | null,
  meta: Pick<
    StudentPortfolioConstructor,
    "createdAt" | "createdBy" | "lastUpdatedAt" | "lastUpdatedBy"
  >
): StudentPortfolioConstructor => {
  const projectsRecord: Record<Uuid, PortfolioProjectStored> = {}
  projects.forEach((p) => {
    projectsRecord[p.id as Uuid] = projectUiToStored(p)
  })
  const projectOrder = projects.map((p) => p.id as Uuid)
  const base: StudentPortfolioConstructor = {
    id: studentId,
    studentId,
    profile: profileUiToStored(portfolioProfile),
    projects: projectsRecord,
    projectOrder,
    priorityLaneProjectIds: priorityLaneProjectIds as Uuid[],
    timelineEntries: timelineUiToStored(timelineEntries),
    ...meta,
  }
  if (existing) {
    return {
      ...existing,
      ...base,
      createdAt: existing.createdAt,
      createdBy: existing.createdBy,
    }
  }
  return base
}

/** Preserves `projectOrder` array order and step `sortIndex` order from Firestore. */
export const normalizeProjectsForUi = (
  projects: PortfolioMockProject[]
): PortfolioMockProject[] =>
  projects.map((project) => ({
    ...project,
    stages: sortBy(project.stages, (stage) => stage.sortIndex ?? 0),
  }))
